import { Clear as ClearIcon } from 'components/icons';
import InfoTooltip from 'components/InfoTooltip';
import { Button } from 'components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import isArray from 'lodash/isArray';
import isEqual from 'lodash/isEqual';
import React, { useCallback, useEffect, useMemo } from 'react';
import { t } from 'utils/getTranslations.ts';
import { getSelectorKey } from '../../getSelectorKey.ts';
import { MainFilterForm, QuickFilterForm, SelectOption, TextFilterForm, TextMatch } from '../../ListTableTypes.tsx';
import ConditionSwitch from '../../filter-bar/ConditionSwitch';
import { useIsNewDesign } from '../../filter-bar/useIsNewDesign.ts';
import { ClearableInput } from './ClearableInput';
import { FilterLabel } from './FilterLabel';
import { CollapsedTags } from './filterSelectParts';
import { useOptionsInactiveMaxRows } from './hooks/useOptionsInactiveMaxRows.ts';

const MATCHES: TextMatch[] = ['any', 'all', 'none'];
const MATCH_LABEL_IDS: Record<TextMatch | TextEmptiness, string> = {
    any: 'filters.contains-any',
    all: 'filters.contains-all',
    none: 'filters.contains-none',
    empty: 'filters.is-empty-option',
    'not-empty': 'filters.is-not-empty-option',
};
const MATCH_SEGMENT_IDS: Record<TextMatch, string> = {
    any: 'filters.match-any',
    all: 'filters.match-all',
    none: 'filters.match-none',
};

/** The classic Match select's emptiness choices, on a field with an `emptyComparator`; the new design's switch has them. */
export type TextEmptiness = 'empty' | 'not-empty';

/**
 * Whether the terms match any, all or none: a compact select after "Match" in the classic label, a row under the terms
 * in the new design.
 */
function MatchControl({
    filter,
    inLabel,
    onChange,
}: {
    filter: TextFilterForm;
    /** Drawn in the classic label, or under the terms in the new design: each spot draws it for its design. */
    inLabel: boolean;
    onChange: (match: TextMatch | TextEmptiness) => void;
}) {
    const isNewDesign = useIsNewDesign();
    if (isNewDesign === inLabel) return null;
    const value = filter.match ?? 'any';
    const holdsTerms = !!filter.value?.length;
    const choices: (TextMatch | TextEmptiness)[] = filter.emptyComparator
        ? [...MATCHES, 'empty', 'not-empty']
        : MATCHES;
    const selected = holdsTerms ? value : filter.isEmpty ? 'empty' : filter.isNotEmpty ? 'not-empty' : value;
    const name = t('filters.match-for', { label: filter.label ?? '' });
    const tooltip = filter.controlTooltips?.match ? (
        <InfoTooltip text={filter.controlTooltips.match} label={name} className="h-4" />
    ) : null;
    if (isNewDesign) {
        return (
            <div className="flex items-center gap-2">
                <span className="text-foreground-tertiary text-meta">{t('filters.match')}</span>
                <ConditionSwitch
                    aria-label={name}
                    items={MATCHES.map((match) => ({ value: match, label: t(MATCH_SEGMENT_IDS[match]) }))}
                    value={value}
                    onChange={onChange}
                />
                {tooltip}
            </div>
        );
    }
    return (
        <span className="ml-auto flex items-center gap-1">
            <span className="text-caption text-fg-secondary">{t('filters.match')}</span>
            <Select
                items={choices.map((match) => ({ value: match, label: t(MATCH_LABEL_IDS[match]) }))}
                value={selected}
                onValueChange={(next) => next && onChange(next as TextMatch | TextEmptiness)}
            >
                <SelectTrigger
                    size="sm"
                    aria-label={name}
                    className="bg-paper text-caption text-fg w-auto gap-1 py-0 pr-1 pl-2 data-[size=sm]:h-6 [&_svg]:size-3.5"
                >
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    {choices.map((match) => (
                        <SelectItem key={match} value={match}>
                            {t(MATCH_LABEL_IDS[match])}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
            {tooltip}
        </span>
    );
}

export const Text: React.FC<{
    filter: TextFilterForm;
    quickFilters: QuickFilterForm;
    mainFilters: MainFilterForm;
    onChange: (value: string | string[], groupSelector: string) => void;
    onMatchChange?: (match: TextMatch | TextEmptiness, groupSelector: string) => void;
}> = React.memo(({ filter, onChange, onMatchChange, quickFilters, mainFilters }) => {
    const matchControl = (inLabel: boolean) =>
        filter.matchModes && onMatchChange ? (
            <MatchControl
                filter={filter}
                inLabel={inLabel}
                onChange={(match) => onMatchChange(match, getSelectorKey(filter))}
            />
        ) : null;
    const filterValue = filter.value;
    // The tags are read from the filter state on every render, so a reset made while the field is
    // mounted (the panel's "Clear all filters") shows at once.
    const value = useMemo<string[]>(
        () => (filterValue !== null ? (isArray(filterValue) ? filterValue : filterValue.split(',')) : []),
        [filterValue]
    );
    const [inputValue, setInputValue] = React.useState('');
    // The filter value as this field last saw or set it. Any other value came from outside, and the
    // pending text goes with it. Compared by content: re-seeding the filter state from the URL
    // rebuilds the same terms as a new array, and that is no change.
    const [seenValue, setSeenValue] = React.useState(filterValue);
    if (!isEqual(filterValue, seenValue)) {
        setSeenValue(filterValue);
        setInputValue('');
    }
    const isMultiple = filter.multiple === true;
    const commitOnEnter = filter.commitOnEnter === true;

    const commit = useCallback(
        (next: string[]) => {
            setSeenValue(next);
            onChange(next, getSelectorKey(filter));
        },
        [filter, onChange]
    );

    const isHidden =
        filter.getShouldHide !== undefined
            ? filter.getShouldHide({
                  mainFilters,
                  quickFilters,
                  groupSelector: getSelectorKey(filter),
              })
            : false;

    useEffect(() => {
        if (isHidden && !filter?.preserveValueWhenHidden && value.length > 0) {
            // eslint-disable-next-line react-hooks-js/set-state-in-effect -- side-effect reset: clear the value in the parent (onChange) when filter becomes hidden
            commit([]);
        }
    }, [filter?.preserveValueWhenHidden, isHidden, value.length, commit]);

    const handleChange = useCallback(
        (inputValue: string, isBlur = false) => {
            // commitOnEnter leaves the pending input alone until Enter or blur, so a comma inside a
            // term stays part of it.
            const options = isBlur || commitOnEnter ? inputValue : inputValue.split(',');

            if ((Array.isArray(options) && options.length > 1) || (options.length > 0 && isBlur)) {
                const newVal = value.concat(options).flatMap((x) => {
                    const trimmed = x.trim();
                    return trimmed ? [trimmed] : [];
                });

                setInputValue(isBlur ? inputValue : '');
                commit(newVal);
            } else {
                setInputValue(inputValue);
            }
        },
        [value, commit, commitOnEnter]
    );

    const { focused, setFocused, hiddenCount, visibleCount, measureRef } = useOptionsInactiveMaxRows({
        selectedOptions: value as unknown as SelectOption[],
    });

    if (isHidden) {
        return null;
    }

    return (
        <div className="flex grow flex-col gap-2" key={`filter-${filter.selector}`}>
            {filter.label && (
                <FilterLabel label={filter.label} tooltip={filter.tooltip} tooltipValues={filter.tooltipValues}>
                    {matchControl(true)}
                </FilterLabel>
            )}
            {isMultiple ? (
                // A tags field: typed terms become tags, split on commas (or committed with Enter under
                // `commitOnEnter`), and the pending text is committed when the field loses focus.
                // Backspace in an empty input takes out the last tag; each tag also has its own remove.
                <div className="border-input bg-paper focus-within:border-ring focus-within:ring-ring/50 relative flex min-h-9 flex-wrap items-center gap-1.5 rounded-md border bg-clip-padding py-1.5 pr-9 pl-2.5 text-sm shadow-xs transition-[color,box-shadow] focus-within:ring-3">
                    <CollapsedTags
                        values={value}
                        getLabel={(term) => String(term)}
                        onDelete={(index) => commit(value.filter((_, position) => position !== index))}
                        showAll={focused}
                        visibleCount={visibleCount}
                        hiddenCount={hiddenCount}
                        measureRef={measureRef}
                    />
                    <input
                        id={`text-multi-${getSelectorKey(filter)}`}
                        aria-label={filter.label}
                        className="placeholder:text-muted-foreground min-w-16 flex-1 bg-transparent text-base/[1.25] outline-none md:text-sm"
                        value={inputValue}
                        placeholder={t(commitOnEnter ? 'search-enter-delimiter' : 'search-comma-delimiter')}
                        onFocus={() => setFocused(true)}
                        onChange={(event) => handleChange(event.target.value)}
                        onKeyDown={(event) => {
                            if (event.key === 'Backspace' && inputValue === '' && value.length > 0) {
                                commit(value.slice(0, -1));
                            } else if (commitOnEnter && event.key === 'Enter' && inputValue.trim()) {
                                event.preventDefault();
                                handleChange(inputValue, true);
                                setInputValue('');
                            }
                        }}
                        onBlur={(event) => {
                            setFocused(false);
                            handleChange(String(event.target.value), true);
                            setInputValue('');
                        }}
                    />
                    {value.length > 0 ? (
                        <Button
                            variant="ghost"
                            size="icon-xs"
                            aria-label={t('clear')}
                            // Pinned to the field's right edge, clear of the tags.
                            className="text-fg-secondary absolute top-1/2 right-2 size-5 -translate-y-1/2"
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => commit([])}
                        >
                            <ClearIcon className="size-3.5" />
                        </Button>
                    ) : null}
                </div>
            ) : (
                <ClearableInput
                    value={typeof filter?.value === 'string' ? filter.value : ''}
                    placeholder={t('search')}
                    ariaLabel={filter.label}
                    onClear={() => onChange('', getSelectorKey(filter))}
                    onChange={(e) => onChange(String(e.target.value), getSelectorKey(filter))}
                />
            )}
            {matchControl(false)}
        </div>
    );
});
